import styles from './MailboxExchangeSynchronization.module.scss';

import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactExportModeCheckboxes } from '@work4all/components/lib/components/entity-preview/user-preview/components/ContactExportModeCheckboxes';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { Caption } from '@work4all/components/lib/typography/caption/Caption';

import { useTenant } from '@work4all/data/lib/hooks/routing/TenantProvider';

import { MailboxFeatures } from '@work4all/models/lib/Enums/MailboxFeatures.enum';
import { SyncOptionsContactExport } from '@work4all/models/lib/Enums/SyncOptionsContactExport.enum';

import { useMailboxesData } from '../../../../../hooks/use-mailboxes-data';
import { MailboxPickerField } from '../../../../entity-picker/MailboxPickerField';
import {
  useMailboxFeatures,
  useUserMailboxExchangeSynchronizationOptions,
} from '../hooks';

export const MailboxExchangeSynchronization = () => {
  const { t } = useTranslation();

  const tenantId = useTenant();

  const {
    allowExportingCustomer,
    allowExportingSupplier,
    allowExportingEmployees,
    contactsExportMode,
    contactsExportAllowed: contactsAllowedExportMode,
    includeContactsPrivateData,
    allowSyncingAppointments,
    allowSyncingPrivateAppointments,
    mailboxId,
    contactsExportCustomerAllowed,
    contactsExportEmployeesAllowed,
    contactsExportSupplierAllowed,
    appointmentsSyncExternalAppointments,
  } = useUserMailboxExchangeSynchronizationOptions({
    tenantId: tenantId.activeTenant,
  });

  const mailboxData = useMailboxesData();
  const mailboxFeatures = useMailboxFeatures();
  const chosenMailbox = useMemo(() => {
    return mailboxData.mailboxes?.find((x) => x.id === mailboxId.value);
  }, [mailboxData.mailboxes, mailboxId.value]);

  const missingMailboxFeature = useMemo(
    () =>
      !mailboxFeatures.hasFeature(
        chosenMailbox?.id,
        MailboxFeatures.READ_WRITE_CONTACTS
      ),
    [chosenMailbox?.id, mailboxFeatures]
  );
  const maskDisabled = useMemo(
    () => missingMailboxFeature || !chosenMailbox,
    [chosenMailbox, missingMailboxFeature]
  );

  return (
    <>
      <Stack direction="column" spacing={2} padding="2.5rem">
        <Typography variant="h4">
          {t('EXCHANGE_SYNCHRONIZATION.TITLE')}
        </Typography>

        <div>
          <MailboxPickerField
            clearable
            value={
              chosenMailbox
                ? {
                    ...chosenMailbox,
                    smtpAddress: chosenMailbox?.mailboxPrimaryAddress,
                  }
                : null
            }
            onChange={(e) => mailboxId.set(e?.id || null)}
          />
          {chosenMailbox && missingMailboxFeature && (
            <Caption color="error">
              {t('EXCHANGE_SYNCHRONIZATION.MAILBOX.TOOLTIP')}
            </Caption>
          )}
          {!chosenMailbox && (
            <Caption color="error">
              {t('EXCHANGE_SYNCHRONIZATION.CHOOSE_MAILBOX')}
            </Caption>
          )}
        </div>

        <div>
          <Divider>
            {t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.TITLE')}
          </Divider>

          <CheckboxRadioItem
            control="checkbox"
            label={t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.CUSTOMERS')}
            disabled={maskDisabled || !contactsExportCustomerAllowed.value}
            checked={
              contactsExportCustomerAllowed.value &&
              allowExportingCustomer.value
            }
            onChange={() => {
              allowExportingCustomer.set(!allowExportingCustomer.value);
            }}
          />

          <CheckboxRadioItem
            control="checkbox"
            label={t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.SUPPLIERS')}
            disabled={maskDisabled || !contactsExportSupplierAllowed.value}
            checked={
              contactsExportSupplierAllowed.value &&
              allowExportingSupplier.value
            }
            onChange={() => {
              allowExportingSupplier.set(!allowExportingSupplier.value);
            }}
          />

          <CheckboxRadioItem
            control="checkbox"
            label={t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.EMPLOYEES')}
            disabled={maskDisabled || !contactsExportEmployeesAllowed.value}
            checked={
              contactsExportEmployeesAllowed.value &&
              allowExportingEmployees.value
            }
            onChange={() => {
              allowExportingEmployees.set(!allowExportingEmployees.value);
            }}
          />
        </div>

        <div>
          <Divider>{t('EXCHANGE_SYNCHRONIZATION.CONTACT_TYPES.TITLE')}</Divider>

          <ContactExportModeCheckboxes
            mode={contactsExportMode.value}
            maximumAllowedMode={contactsAllowedExportMode.value}
            onChange={contactsExportMode.set}
            disabled={maskDisabled}
          />

          <CheckboxRadioItem
            control="checkbox"
            label={t(
              'EXCHANGE_SYNCHRONIZATION.CONTACT_TYPES.INCL_PRIVATE_ADDRESSES_AND_EMAIL_AND_TELEPHONE'
            )}
            checked={
              contactsAllowedExportMode.value !==
                SyncOptionsContactExport.NO_EXPORT &&
              includeContactsPrivateData.value
            }
            onChange={() => {
              includeContactsPrivateData.set(!includeContactsPrivateData.value);
            }}
            disabled={
              maskDisabled ||
              contactsAllowedExportMode.value ===
                SyncOptionsContactExport.NO_EXPORT
            }
          />
        </div>

        <div>
          <Divider>{t('EXCHANGE_SYNCHRONIZATION.APPOINTMENTS.TITLE')}</Divider>

          <CheckboxRadioItem
            control="checkbox"
            label={t(
              'EXCHANGE_SYNCHRONIZATION.APPOINTMENTS.ALLOW_SYNCHRONIZATION'
            )}
            checked={allowSyncingAppointments.value}
            onChange={() => {
              allowSyncingAppointments.set(!allowSyncingAppointments.value);
            }}
            disabled={maskDisabled}
          />

          <CheckboxRadioItem
            label={t('USER.EXCHANGE.IMPORT_MANUAL')}
            checked={
              allowSyncingAppointments.value &&
              appointmentsSyncExternalAppointments.value
            }
            onChange={() =>
              appointmentsSyncExternalAppointments.set(
                !appointmentsSyncExternalAppointments.value
              )
            }
            disabled={!allowSyncingAppointments.value || maskDisabled}
          />
          <Box pl="1rem">
            <CheckboxRadioItem
              control="checkbox"
              label={t(
                'EXCHANGE_SYNCHRONIZATION.APPOINTMENTS.EXCLUDE_PRIVATE_APPOINTMENTS'
              )}
              checked={
                allowSyncingAppointments.value &&
                appointmentsSyncExternalAppointments.value &&
                !allowSyncingPrivateAppointments.value
              }
              onChange={() => {
                allowSyncingPrivateAppointments.set(
                  !allowSyncingPrivateAppointments.value
                );
              }}
              disabled={
                maskDisabled ||
                !allowSyncingAppointments.value ||
                !appointmentsSyncExternalAppointments.value
              }
            />
          </Box>
        </div>
      </Stack>

      <div className={styles.bottomWrapper}>
        <Alert
          variant="filled"
          severity="error"
          color="warning"
          className={styles.alert}
        >
          {t('EXCHANGE_SYNCHRONIZATION.ALERT')}
        </Alert>
      </div>
    </>
  );
};
